import { z } from "zod";
import { FILE_KINDS } from "./container.js";
/**
 * Multipart metadata for a media upload (POST /containers/:id/files).
 * The binary streams to disk; this describes the file entry appended
 * to container.json (ADR-0002 / ADR-0008). Multipart values arrive as
 * strings, hence the coerced numbers. The capture location (ticket 04)
 * travels as three flat fields and is composed into the entry by the
 * route when all three are present.
 */
export const fileUploadSchema = z.object({
    kind: z.enum(FILE_KINDS, {
        errorMap: () => ({ message: `kind must be one of ${FILE_KINDS.join(", ")}` }),
    }),
    name: z.string().min(1).optional(),
    /** Optimistic concurrency: the container version the upload was built from. */
    version: z.coerce.number().int().nonnegative(),
    latitude: z.coerce.number().min(-90).max(90).optional(),
    longitude: z.coerce.number().min(-180).max(180).optional(),
    capturedAt: z.string().datetime().optional(),
});
/**
 * Optimistic-concurrency body for removing a file
 * (DELETE /containers/:id/files/:fileId, ADR-0008).
 */
export const removeFileSchema = z.object({
    version: z.number().int().nonnegative(),
});
/**
 * Body of PUT /containers/:id/files/:fileId/manual-text (ADR-0035): the human
 * correction of an audio file's auto transcript. An empty string is a valid
 * override — presence of the field, not its content, is the signal.
 */
export const manualTextInputSchema = z.object({
    text: z.string(),
});
/**
 * File extension for a mime type (ADR-0003 rules first, pragmatic fallbacks
 * for real-world picker files after — e.g. HEIC photos, mp3 memos).
 */
export function extensionForMime(mime) {
    switch (mime.toLowerCase()) {
        case "image/jpeg":
            return "jpg";
        case "image/png":
            return "png";
        case "audio/webm":
            return "webm";
        case "audio/opus":
            return "opus";
        case "audio/ogg":
            return "ogg";
        case "audio/mpeg":
            return "mp3";
        case "audio/mp4":
            return "m4a";
        default: {
            const sub = mime.split("/")[1];
            const clean = sub ? sub.replace(/[^a-z0-9]/gi, "").slice(0, 8) : "";
            return clean || "bin";
        }
    }
}
/** Whether a mime fits the file kind: photo → image/*, audio → audio/*. */
export function mimeMatchesKind(kind, mime) {
    const prefix = kind === "photo" ? "image/" : "audio/";
    return mime.toLowerCase().startsWith(prefix);
}
//# sourceMappingURL=file.js.map